import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ButtonSpinner } from '../../../../helpers/UI/ButtonSpinner/ButtonSpinner';
import type {
  CreateSocialSubmissionRequest,
  SocialMediaPlatform,
  SocialSubmission,
  SocialSubmissionEventType,
} from '../../../../helpers/utils/types';

const SOCIAL_EVENT_TYPES: readonly SocialSubmissionEventType[] = [
  'Social Shout Out',
  'Social Follow',
];
const SOCIAL_PLATFORMS: readonly SocialMediaPlatform[] = ['Instagram', 'TikTok'];

const EVENT_TYPE_LABELS: Record<SocialSubmissionEventType, string> = {
  'Social Shout Out': 'Social Shout-Out',
  'Social Follow': 'Social Follow',
};

type FormErrors = Partial<Record<'eventType' | 'platform' | 'username' | 'postUrl', string>>;

type AddSocialSubmissionModalProps = {
  submissionToEdit?: SocialSubmission;
  submitting: boolean;
  errorText: string | null;
  onClose: () => void;
  onErrorDismiss: () => void;
  onSubmit: (body: CreateSocialSubmissionRequest) => void;
};

const isValidHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const inputClass =
  'mt-1.5 w-full rounded-[12px] border border-[#E2E8F0] bg-white px-3.5 py-2.5 text-[14px] text-[#0A0B14] outline-none focus:border-[#0055FF]';

export const AddSocialSubmissionModal = ({
  submissionToEdit,
  submitting,
  errorText,
  onClose,
  onErrorDismiss,
  onSubmit,
}: AddSocialSubmissionModalProps) => {
  const isEditing = !!submissionToEdit;
  const [eventType, setEventType] = useState<SocialSubmissionEventType | ''>(
    submissionToEdit?.eventType ?? ''
  );
  const [platform, setPlatform] = useState<SocialMediaPlatform | ''>(
    submissionToEdit?.socialMediaType.name ?? ''
  );
  const [username, setUsername] = useState(submissionToEdit?.username ?? '');
  const [postUrl, setPostUrl] = useState(submissionToEdit?.postUrl ?? '');
  const [errors, setErrors] = useState<FormErrors>({});

  const isShoutOut = eventType === 'Social Shout Out';

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [submitting, onClose]);

  const handleEventTypeChange = (value: SocialSubmissionEventType | '') => {
    onErrorDismiss();
    setEventType(value);
    setPlatform('');
    setUsername('');
    setPostUrl('');
    setErrors({});
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!eventType) next.eventType = 'Please select an event type.';
    if (!platform) next.platform = 'Please select a platform.';
    if (!username.trim()) next.username = 'Please enter your username.';
    if (isShoutOut) {
      if (!postUrl.trim()) next.postUrl = 'Please enter the post URL.';
      else if (!isValidHttpUrl(postUrl.trim())) {
        next.postUrl = 'Please enter a valid URL, starting with http:// or https://.';
      }
    }
    return next;
  };

  const handleSubmit = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || !eventType || !platform) return;

    onSubmit({
      eventType,
      platform,
      username: username.trim(),
      ...(isShoutOut ? { postUrl: postUrl.trim() } : {}),
    });
  };

  const errorId = (field: keyof FormErrors) => `social-submission-${field}-error`;

  const getFieldProps = (field: keyof FormErrors) => ({
    id: `social-submission-${field}`,
    name: field,
    'aria-invalid': errors[field] ? true : undefined,
    'aria-describedby': errors[field] ? errorId(field) : undefined,
  });

  const renderError = (field: keyof FormErrors) =>
    errors[field] ? (
      <p id={errorId(field)} className='mt-1 text-[12px] text-red-600' role='alert'>
        {errors[field]}
      </p>
    ) : null;

  const labelClass = 'block text-[13px] font-semibold text-[#0A0B14]';

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40'
      role='presentation'
      onClick={submitting ? undefined : onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='social-submission-modal-title'
        className='relative w-full max-w-[460px] rounded-[20px] bg-[#F4F6FB] p-6 shadow-[0_24px_80px_rgba(15,23,42,0.18)]'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type='button'
          onClick={onClose}
          disabled={submitting}
          className='absolute right-4 top-4 rounded-full p-1.5 text-[#697386] hover:bg-white/80 transition-colors'
          aria-label='Close'
        >
          <X size={20} />
        </button>

        <h2
          id='social-submission-modal-title'
          className='font-display text-[22px] font-semibold text-[#0A0B14] tracking-[-0.02em]'
        >
          {isEditing ? 'Edit submission' : 'Add submission'}
        </h2>

        <div className='mt-5 space-y-4'>
          <div>
            <label htmlFor='social-submission-eventType' className={labelClass}>Event type</label>
            <select
              {...getFieldProps('eventType')}
              disabled={isEditing}
              className={inputClass}
              value={eventType}
              onChange={(e) => handleEventTypeChange(e.target.value as SocialSubmissionEventType | '')}
            >
              <option value=''>Select an event</option>
              {SOCIAL_EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {EVENT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
            {renderError('eventType')}
          </div>

          {eventType && (
            <>
              {isShoutOut && (
                <div>
                  <label htmlFor='social-submission-postUrl' className={labelClass}>Post URL</label>
                  <input
                    {...getFieldProps('postUrl')}
                    type='url'
                    className={inputClass}
                    placeholder='https://www.instagram.com/p/abc123'
                    value={postUrl}
                    onChange={(e) => {
                      onErrorDismiss();
                      setPostUrl(e.target.value);
                    }}
                  />
                  {renderError('postUrl')}
                </div>
              )}

              <div>
                <label htmlFor='social-submission-platform' className={labelClass}>Platform</label>
                <select
                  {...getFieldProps('platform')}
                  disabled={isEditing}
                  className={inputClass}
                  value={platform}
                  onChange={(e) => {
                    onErrorDismiss();
                    setPlatform(e.target.value as SocialMediaPlatform | '');
                  }}
                >
                  <option value=''>Select a platform</option>
                  {SOCIAL_PLATFORMS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {renderError('platform')}
              </div>

              <div>
                <label htmlFor='social-submission-username' className={labelClass}>Username</label>
                <input
                  {...getFieldProps('username')}
                  type='text'
                  className={inputClass}
                  placeholder='@yourusername'
                  maxLength={255}
                  value={username}
                  onChange={(e) => {
                    onErrorDismiss();
                    setUsername(e.target.value);
                  }}
                />
                {renderError('username')}
              </div>
            </>
          )}
        </div>

        {isEditing && submissionToEdit?.reviewerComment && (
          <p className='mt-4 rounded-[12px] bg-white px-3.5 py-2.5 text-[13px] text-[#0A0B14]'>
            <span className='font-semibold'>Reviewer comment: </span>
            {submissionToEdit.reviewerComment}
          </p>
        )}

        {errorText && (
          <p className='mt-4 text-center text-[13px] text-red-600' role='alert'>
            {errorText}
          </p>
        )}

        <button
          type='button'
          disabled={submitting || !eventType}
          onClick={handleSubmit}
          className='mt-6 flex w-full items-center justify-center rounded-[14px] bg-[#0055FF] px-5 py-3.5 text-[15px] font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60'
        >
          {submitting ? <ButtonSpinner /> : isEditing ? 'Resubmit' : 'Submit'}
        </button>
      </div>
    </div>
  );
};
